import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";
import { DarkgroupColors, groupColors, IPolarAreaChart, playermetricsGroup, SegmentData, teammetricsGrouped } from "../../../types/IRolesPlayedUtils";

export const formatLabel = (text: string, isTeam: boolean): string => {
  // if (text.toLowerCase().startsWith("xg")) {
  //   return "xG Shot".toUpperCase();
  // }
  // if(isTeam && text.toLocaleLowerCase() == "xgshotagainst"){
  //   return "xg shot against".toUpperCase();
  // }
  if (text.toLowerCase() === "interceptions") {
    return "Interceptions & Blocks".toUpperCase();
  }
  if (text.toLowerCase() === "passestofinalethird") {
    return "Passes To Final Third".toUpperCase();
  }
  if (isTeam && text.toLowerCase() === "dangerousopponenthalfrecoveries") {
    return "DANGEROUS BALL RECOVERIES".toUpperCase();
  }
  if (isTeam && text.toLowerCase() === "offensiveduels") {
    return "Off. Duels".toUpperCase();
  }
  // if (isTeam && text.toLowerCase() === "forwardpasses") {
  //   return "% FORWARD PASSES".toUpperCase();
  // }
  // if (isTeam && text.toLowerCase() === "ballrecoveries") {
  //   return "Pressing".toUpperCase();
  // }
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
    .toUpperCase();
};
 
export const PolarAreaChart: React.FC<IPolarAreaChart> = ({ data, width, height, colorIndex , averageData , isForComparison ,isComparisonBlackLine}) => {
  const svgRef = useRef(null);
  const{clubCompareId}= useClubSearch()
  const {comparePlayerId} = usePlayerSearch()

  useEffect(() => {
    if (!data || !data.player  || !data?.average  ) return;

    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const radius = Math.min(innerWidth, innerHeight) / 2;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const dataLength = (clubCompareId > 0 && isForComparison) ? averageData?.player?.length : data.player?.length;
    const angleScale = d3
      .scaleLinear()
      .domain([0, dataLength!])
      .range([0, 2 * Math.PI]);
     
    const radiusScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([0, radius]);
      
    const segments = data.player.map((d, i) => ({
      startAngle: angleScale(i),
      endAngle: angleScale(i + 1),
      value: d.value,
      segment: d.segment,
      rawScore: d.rawScore,
    }));

    const compareSegments = data.player.map((d, i) => {
      const matching = averageData?.player.find(avg => avg.segment === d.segment);    
      return {
        startAngle: angleScale(i),
        endAngle: angleScale(i + 1),
        value: matching?.value ?? 0,         // fallback if segment is missing
        segment: d.segment,
        rawScore: matching?.rawScore ?? 0,
      };
    });

    const compareSegmentsBorderData = ((clubCompareId > 0 && isForComparison) || comparePlayerId > 0) ? compareSegments : data?.average;
    const metricsGrouped = data.isTeam ? teammetricsGrouped : playermetricsGroup

    // Draw background segments
    svg
      .selectAll(".segment")
      .data(segments)
      .join("path")
      .attr("class", "segment")
      .attr( "d",d3
          .arc<SegmentData>()
          .innerRadius(8)
          .outerRadius((d) => radiusScale(d.value))
          .startAngle((d) => d.startAngle)
          .endAngle((d) => d.endAngle)
      )
      .style("fill", (d: any) => {
          if (metricsGrouped.finalThird.includes(d.segment)) {
            return groupColors.finalThird;
          } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
            return groupColors.buildUpPlay;
          } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
            return groupColors.defensivePlay;
          }
          return "#D3D3D3"; 
      })
      .style("stroke", "white")
      .style("stroke-width", 1)
      .attr("pointer-events", "none");

      svg
        .selectAll(".segment-lines")
        .data(segments)
        .join("path")
        .attr("class", "segment-lines")
        .attr("d", (d) => {
          const startPoint = d3
            .arc<SegmentData>()
            .innerRadius(radiusScale(d.value))
            .outerRadius(radiusScale(d.value))
            .startAngle(d.startAngle)
            .endAngle(d.startAngle)(d);
          const endPoint = d3
            .arc<SegmentData>()
            .innerRadius(radius)
            .outerRadius(radius)
            .startAngle(d.startAngle)
            .endAngle(d.startAngle)(d);
          return `M ${startPoint!.split("M")[1]} L ${endPoint!.split("M")[1]}`;
        })
        .style("stroke", "#DADADA")
        .style("stroke-width", 1);
      
      //black border
      if(isComparisonBlackLine){
      svg
        .selectAll(".average-border")
        .data(compareSegmentsBorderData!)
        .join("path")
        .attr("class", "average-border")
        .attr("d", (d, i) => {
          const startAngle = angleScale(i);
          const endAngle = angleScale(i + 1);
          const currentRadius = radiusScale(d.value);
          const nextRadius = radiusScale(compareSegmentsBorderData![(i + 1) % compareSegmentsBorderData?.length!].value);
          // Calculate current arc points
          const x1 = Math.sin(startAngle) * currentRadius;
          const y1 = -Math.cos(startAngle) * currentRadius;
          const x2 = Math.sin(endAngle) * currentRadius;
          const y2 = -Math.cos(endAngle) * currentRadius;
          // Calculate next arc's starting point
          const x3 = Math.sin(endAngle) * nextRadius;
          const y3 = -Math.cos(endAngle) * nextRadius;
          // Create path: Move to start -> Draw arc -> Draw line to next radius
          return `M ${x1},${y1} 
                  A ${currentRadius},${currentRadius} 0 0,1 ${x2},${y2}
                  L ${x3},${y3}`;
        })
        .style("fill", "none")
        .style("stroke", "#000000")
        .style("stroke-width", 2);
      }

      svg.append("circle").attr("r", radius).style("fill", "none").style("stroke", "#C2C2C2").style("stroke-width", 1);

      // Add labels
      svg
        .selectAll(".label-container")
        .data(segments)
        .join("g")
        .attr("class", "label-container")
        .attr("transform", (d) => {
          const angle = (d.startAngle + d.endAngle) / 2;
          const x = Math.sin(angle) * (radius + 6);
          const y = -Math.cos(angle) * (radius + 6);
          return `translate(${x},${y})`;
        })
        .each(function (d) {
          const container = d3.select(this);
          const words = formatLabel(d.segment, (data.isTeam??false)).toUpperCase().split(" ");
          const lineHeight = 8; // Height for each line of text
          // Create first line with first two words
          const firstLine = container
            .append("text")
            .attr("class", "label")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .text(words.slice(0, 2).join(" "))
            .style("font-family", "DM Mono")
            .style("font-size", "10px")
            .style("font-weight", "500")
            .style("fill", "#7C7D79");
          let bbox = firstLine.node()!.getBBox();
          let totalHeight = bbox.height;
          let maxWidth = bbox.width;
          // If there are more words, create second line
          if (words.length > 2) {
            const secondLine = container
              .append("text")
              .attr("class", "label")
              .attr("text-anchor", "middle")
              .attr("dy", `${lineHeight}px`)
              .text(words.slice(2).join(" "))
              .style("font-family", "DM Mono")
              .style("font-size", "10px")
              .style("font-weight", "500")
              .style("fill", "#7C7D79");
            const secondBbox = secondLine.node()!.getBBox();
            totalHeight = bbox.height + secondBbox.height;
            maxWidth = Math.max(bbox.width, secondBbox.width);
            // Adjust the position of both lines to be centered
            const totalBboxHeight = totalHeight;
            const firstLineY = -totalBboxHeight / 2 + bbox.height / 2;
            const secondLineY = firstLineY + lineHeight;
            firstLine.attr("y", firstLineY);
            secondLine.attr("y", secondLineY);
          }
          // Create background rectangle for label
          container
            .insert("rect", "text")
            .attr("x", -maxWidth / 2 - 8)
            .attr("y", -totalHeight / 2 - 4)
            .attr("width", maxWidth + 16)
            .attr("height", totalHeight + 8)
            .attr("rx", 2)
            .attr("fill", "white");
        });

        //on hover only
        svg.selectAll(".hover-area")
          .data(segments)
          .join("path")
          .attr("class", "hover-area")
          .attr("d",d3.arc<SegmentData>()
                      .innerRadius(8)
                      .outerRadius(radius)
                      .startAngle((d) => d.startAngle)
                      .endAngle((d) => d.endAngle)
          )
          .style("fill", "transparent")
          .style("cursor", "pointer")
          .on("mouseover", function (event, d) {
            svg
              .selectAll(".segment")
              .filter((seg: any) => seg.segment === d.segment)
              .style("filter", "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.25))") // Add box shadow
              .style("fill", (d: any) => {
                // if (data.isTeam) {
                  if (metricsGrouped.finalThird.includes(d.segment)) {
                    return DarkgroupColors.finalThird;
                  } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
                    return DarkgroupColors.buildUpPlay;
                  } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
                    return DarkgroupColors.defensivePlay;
                  }
                  return "#D3D3D3"; // Default color for ungrouped metrics
                // }
                // return archetypeColorIndexes[colorIndex];
              })
              // .style("fill",data.isTeam ? "#DADADA" : archetypeColorIndexes[colorIndex])
        
            const tooltip = svg.append("g").attr("class", "tooltip-spider").style("pointer-events", "none");
            const angle = (d.startAngle + d.endAngle) / 2;
            const tooltipRadius = radiusScale(d.value) / 2;
            const x = Math.sin(angle) * tooltipRadius;
            const y = -Math.cos(angle) * tooltipRadius;
            const tooltipText =
              d.segment === "pressing"? `${d?.rawScore!.toFixed(2)} PPDA` 
              : (d.segment === "passLength" || d.segment === "dribbleDistanceFromOpponentGoal"
                ? d?.rawScore!.toFixed(2)
                : `${d?.rawScore!.toFixed(2)} per '90`);
            const text = tooltip
              .append("text")
              .attr("text-anchor", "middle")
              .attr("dy", "0.35em")
              .text(tooltipText)
              .style("font-family", "Aeonik")
              .style("font-size", "16px")
              .style("font-weight", "500")
              .style("fill", "#000000");
            const bbox = text.node()!.getBBox();
            tooltip
              .insert("rect", "text")
              .attr("x", bbox.x - 8)
              .attr("y", bbox.y - 4)
              .attr("width", bbox.width + 16)
              .attr("height", bbox.height + 8)
              .attr("rx", 2)
              .attr("fill", "white")
              .style("filter", "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))");
            tooltip.attr("transform", `translate(${x},${y})`);
          })
          .on("mouseout", function (event, d) {
            svg
              .selectAll(".segment")
              .filter((seg: any) => seg.segment === d.segment)
              .style("filter", "none")
              .style("fill", (d: any) => {
                // if (data.isTeam) {
                  if (metricsGrouped.finalThird.includes(d.segment)) {
                    return groupColors.finalThird;
                  } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
                    return groupColors.buildUpPlay;
                  } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
                    return groupColors.defensivePlay;
                  }
                  return "#D3D3D3"; // Default color for ungrouped metrics
                // }
                // return archetypeColorIndexes[colorIndex];
              })
            svg.selectAll(".tooltip-spider").remove();
          });
    }, [data, width, height , data?.average , averageData?.average]);
    return <svg ref={svgRef}></svg>;
};
